/*
Create Pay Calendar Step 2 - Payday information.

Allows a user select how employees are paid, the start date and the pay day. 
*/

import { forwardRef, RefObject, useEffect } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Portal,
  Stack,
  InputGroup,
  Input,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  DropdownInput,
  CrossOutline,
  DatePickerInput,
  ArrowRightOutline,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';
import { useCreatePayCalendarStore } from 'app/store';
import { payCalendarPaydayInformationFormSchema } from 'app/utils/schemas';

import { getPaydayOptions, getPayTypeOptions, getWorkingDaysOptions } from '../../../options';

import {
  PayCalendarDefineCalendarFormFields,
  PayCalendarPaydayInformationFormFields,
  PayCalendarFormProps,
} from '../types';

const PayCalendarPaydayInformationForm = forwardRef<HTMLElement, PayCalendarFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const payCalendarData = useCreatePayCalendarStore((state) => state.payCalendarData);
    const setPayCalendarData = useCreatePayCalendarStore((state) => state.setPayCalendarData);

    const { payFrequency } = payCalendarData as PayCalendarDefineCalendarFormFields; // bring in the payFrequency value

    const {
      register,
      control,
      handleSubmit,
      watch,
      resetField,
      setFocus,
      getValues,
      trigger,
      formState: { errors, isValid },
    } = useForm<PayCalendarPaydayInformationFormFields>({
      mode: 'onChange',
      defaultValues: payCalendarData as PayCalendarPaydayInformationFormFields,
      resolver: yupResolver(payCalendarPaydayInformationFormSchema),
    });

    useEffect(() => {
      trigger();
    }, [trigger]);

    // set watchers on key fields that determine other options
    const watchPayday = watch('payday');

    // populate the option fields
    const paydayOptions = getPaydayOptions(payFrequency);
    const payTypeOptions = getPayTypeOptions();
    const workingDaysOptions = getWorkingDaysOptions();

    const handleNextStep = (data: PayCalendarPaydayInformationFormFields) => {
      setPayCalendarData({
        ...data,
      });
      onStepAction();
    };

    const resetAndFocusField = (field: keyof PayCalendarPaydayInformationFormFields) => {
      resetField(field, { defaultValue: '' });
      setFocus(field);
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setPayCalendarData(formValues);
      previousStep();
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          <FormControl isInvalid={!!errors?.payType?.message} isRequired>
            <FormLabel>How do you pay</FormLabel>
            <Controller
              control={control}
              name="payType"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Do you pay in-period or in-arrears?"
                  defaultValue={payTypeOptions.find((type) => type.value === value)}
                  options={payTypeOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.payType?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.startDate?.message} isRequired>
            <FormLabel>Start date</FormLabel>
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  placeholder="What is the start date of your first pay calendar?"
                  defaultValue={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            {/* {startDateIsBeforePeriodStart && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {startDateIsBeforePeriodStartWarning}
              </Text>
            )} */}
            <FormErrorMessage>{errors?.startDate?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.payday?.message} isRequired>
            <FormLabel>Pay day</FormLabel>
            <Controller
              control={control}
              name="payday"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="When do you pay your employees?"
                  defaultValue={paydayOptions.find((payday) => payday.value === value)}
                  options={paydayOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.payday?.message}</FormErrorMessage>
          </FormControl>

          {watchPayday === 'LAST_CUSTOM_WEEKDAY' && (
            <FormControl isInvalid={!!errors?.paydayLastCustomWeekday?.message} isRequired>
              <FormLabel>Last week day</FormLabel>
              <Controller
                control={control}
                name="paydayLastCustomWeekday"
                render={({ field: { onChange, onBlur, value } }) => (
                  <DropdownInput
                    placeholder="Select the last week day"
                    defaultValue={workingDaysOptions?.find((day) => day.value === value)}
                    options={workingDaysOptions}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <FormErrorMessage>{errors?.paydayLastCustomWeekday?.message}</FormErrorMessage>
            </FormControl>
          )}
          {watchPayday !== undefined && watchPayday === 'N_DAY_OF_PERIOD' && (
            <FormControl isInvalid={!!errors?.paydayNDayOfPeriod?.message} isRequired>
              <FormLabel>What day of the period</FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  placeholder="Enter the day of the period"
                  {...register('paydayNDayOfPeriod')}
                />
                {(watchPayday === 'N_DAY_OF_PERIOD' ||
                  watchPayday === 'N_BUSINESS_DAY_OF_PERIOD') && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear payday custom day"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('paydayNDayOfPeriod')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.paydayNDayOfPeriod?.message}</FormErrorMessage>
            </FormControl>
          )}
          {watchPayday !== undefined && watchPayday === 'N_BUSINESS_DAY_OF_PERIOD' && (
            <FormControl isInvalid={!!errors?.paydayNBusinessDayOfPeriod?.message} isRequired>
              <FormLabel>What business day of the period</FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  placeholder="Enter the day of the period"
                  {...register('paydayNBusinessDayOfPeriod')}
                />
                {watchPayday === 'N_BUSINESS_DAY_OF_PERIOD' && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear payday custom day"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('paydayNBusinessDayOfPeriod')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.paydayNBusinessDayOfPeriod?.message}</FormErrorMessage>
            </FormControl>
          )}
          {watchPayday !== undefined && watchPayday === 'FIXED_DAY' && (
            <FormControl isInvalid={!!errors?.paydayFixedDay?.message} isRequired>
              <FormLabel>Fixed day</FormLabel>
              <Controller
                control={control}
                name="paydayFixedDay"
                render={({ field: { onChange, onBlur, value } }) => (
                  <DatePickerInput
                    placeholder="What fixed day do you use?"
                    defaultValue={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <FormErrorMessage>{errors?.paydayFixedDay?.message}</FormErrorMessage>
            </FormControl>
          )}
          {(watchPayday === 'CUSTOM_DAY' || watchPayday === 'CUSTOM_DAY_IN_ARREARS') && (
            <FormControl isInvalid={!!errors?.paydayCustomDay?.message} isRequired>
              <FormLabel>Custom day</FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  placeholder="Enter the day of the month"
                  {...register('paydayCustomDay')}
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Clear payday custom day"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('paydayCustomDay')}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors?.paydayCustomDay?.message}</FormErrorMessage>
            </FormControl>
          )}

          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

PayCalendarPaydayInformationForm.displayName = 'PayCalendarPaydayInformationForm';

export default PayCalendarPaydayInformationForm;
